import React, { memo, useCallback, useEffect, useMemo, useRef, } from 'react';
import { LayoutAnimation, Platform, StyleSheet, View, } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { setWalkthroughStatus, setWalkthroughStep, putWalkthroughComplete, } from 'actions';
import { CardView, INVISIBLE_TEXT_COLOR, rem, Spacer, ToolTip, } from 'design-system';
import { selectMaximumInterest, selectPotProductWithTheHighestInterest, } from 'features/pots/reducers/selectors';
import useShouldShowUpgrade from 'features/premium/hooks/useShouldShowUpgrade';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useIsTablet from 'hooks/useIsTablet';
import { useTabNavigation } from 'hooks/navigation/useTabNavigation';
import useStyles from 'hooks/useStyles';
import { TAB_SCREEN_HEADER_HEIGHT } from 'navigators/styles';
import { selectFeatureFlags, selectListAccounts, selectNavigatorConfig, } from 'reducers/selectors';
import { useAppDispatch, useAppSelector, useAppStore } from 'store/hooks';
import { Amplitude } from 'utils';
import createStyleSheets from 'utils/createStyleSheets';
import { useMainStackNavigation, useMainStackRoute, } from 'utils/types/navigationV6';
import { selectIsUserFreeTier } from 'features/premium/selectors/tier';
import { selectShouldShowInvestItemsInWalkthroughV6, selectShowInvestIntroFlag, selectShowSecclFlow, } from 'features/invest/reducers/selectors';
import { selectTabsPriority } from 'reducers/selectors/organiseTabs';
import { useBalances } from 'features/accounts/hooks/useBalances';
import { noop } from 'lodash';
import { useFocusEffect } from 'hooks';
import AccountsHeaderGraph from 'features/accounts/components/AccountsHeaderGraph';
import { useSharedValue } from 'react-native-reanimated';
import Text from 'design-system/Text';
import { tooltipStrings } from 'features/accounts/components/AccountChartInfoView';
import RowTransaction from 'features/transactions/components/RowTransaction';
import SwitchingButton from 'features/subscriptions/components/row/SwitchingButton';
import RowSubscription from 'features/subscriptions/components/row/RowSubscription';
import useColors from 'hooks/useColors';
import LearnCard from 'features/settings/accounts-settings/LearnCard';
import LearnWalkthroughCard from 'features/settings/learn/components/LearnWalkthroughCard';
import { emmaWalkthroughData } from 'features/settings/learn/constants';
import PayTabQuickCTAs from 'features/pay/components/PayTabQuickCTAs';
import { CreditAndDebtSection } from 'features/pay/components/CreditAndDebtSection';
import GroupsSection from 'features/pay/components/GroupsSection';
import { useRemovePayTabExperience } from 'features/pay/hooks/useRemovePayTabExperience';
import { selectHasRentReporting } from 'features/rent-reporting/selectors';
import { selectIsCreditScoreAvailable } from 'features/credit-score/selectors';
import ToolTipContent from 'design-system/ToolTipContent';
import Modal from 'utils/packages/Modal';
import { CHART_TYPE_OPTIONS, CHART_TYPE_OPTIONS_WALKTHROUGH, } from 'features/analytics/constants';
import { selectChartType } from 'features/analytics/selectors';
import { PickerModal } from 'design-system/picker/PickerModal';
import { setChartType } from 'features/analytics/actions';
import { selectFloPaywallC1Inside } from 'features/premium/selectors';
import { selectShouldShowEmmaSetUpFlow } from 'features/setUpEmma/selectors';
import BudgetingCardContainer from './analytics/BudgetingCardContainer';
import IncomeSpendingCard from './analytics/IncomeSpendingCard';
import { completeSetupStepsSelector } from './completeSetup/selectors';
import FeedGroupsSection from './FeedGroupsSection';
import OverviewAddButton from './overview/OverviewAddButton';
import WalkthroughOverviewButtonsRow from './walkthrough/main/WalkthroughOverviewButtonsRow';
import OverviewText from './overview/OverviewText';
import TransactionsSeeAllButton from './transactions/TransactionsSeeAllButton';
import WalkthroughBubbleArrow, { BUBBLE_ARROW_WIDTH, } from './walkthrough/WalkthroughBubbleArrow';
import WalkthroughBubbleContent from './walkthrough/WalkthroughBubbleContent';
import { hasWalkthroughEndStepToShow } from './walkthrough/WalkthroughEndedScreen';
import WalkthroughOverlay from './walkthrough/WalkthroughOverlay';
import Overview from './overview/Overview';
import OverviewRow from './overview/OverviewRow';
import TouchHelper from './walkthrough/TouchHelper';
import Transactions from './transactions/Transactions';
import Subscriptions from './transactions/Subscriptions';
import WalkthroughTransactionDetails from './walkthrough/main/WalkthroughTransactionDetails';
import WalkthroughAnalyticsChartSettingsBtn from './walkthrough/main/WalkthroughAnalyticsChartSettingsBtn';
import WalkthroughIncomeSpendingCard from './walkthrough/main/WalkthroughIncomeSpendingCard';
import WalkthroughSubscriptionScreenItem from './walkthrough/main/WalkthroughSubscriptionScreenItem';
import Quests from './quests/Quests';
import WalkthroughSaveTabItem from './walkthrough/main/WalkthroughSaveTabItem';
import WalkthroughInvestTabItem from './walkthrough/main/WalkthroughInvestTabItem';
import RentReportingSection from './rent-reporting/RentReportingSection';
import { useShowWalkthroughV6 } from './walkthrough/hooks/useShowWalkthroughV6';
const overViewRowHeight = 56;
const WalkthroughContent = ({ itemPositions = {}, isInTab, scrollRef, scrollHeight, scrollLayoutHeight, isFixedPosition, }) => {
    const styles = useStyles(styleSet);
    const navigation = useMainStackNavigation();
    const { navigateToTab } = useTabNavigation();
    const { name: routeName, key: routeKey } = useMainStackRoute();
    const scrollX = useSharedValue(0);
    const { width, contentWidth, paddingHorizontalStyle, paddingHorizontal } = useAppFrameDimensions(isInTab);
    const colors = useColors();
    const isTablet = useIsTablet();
    const potsAccountStatus = useAppSelector((store) => store.pots.account?.status);
    const chartType = useAppSelector(selectChartType);
    const showSecclFlow = useAppSelector(selectShowSecclFlow);
    const shouldShowInvestItemsInWalkthroughV6 = useAppSelector(selectShouldShowInvestItemsInWalkthroughV6);
    const showWalkthroughV6 = useShowWalkthroughV6();
    const removePayTabExperience = useRemovePayTabExperience();
    const hasBudget = useAppSelector((store) => Boolean(store.feed.feed.cards.thisMonth?.budget != null));
    const feed = useAppSelector((store) => store.feed.feed);
    const allowSkipWalkthrough = useAppSelector((store) => store.feed.walkthrough?.allowSkip);
    const didSetupBudgetInOnboarding = useAppSelector((store) => store.signInNavigation.didSetupBudget);
    const hasAccounts = useAppSelector(selectListAccounts).length > 0;
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const isSecclEnabled = useAppSelector(selectShowSecclFlow);
    const maxInterestRate = useAppSelector(selectMaximumInterest);
    const maxInterestProduct = useAppSelector(selectPotProductWithTheHighestInterest);
    const subscriptions = useAppSelector((store) => store.feed.feed.cards.subscriptions);
    const rentReportingData = useAppSelector((store) => store.rentReporting.data);
    const hasRentReporting = useAppSelector(selectHasRentReporting);
    const isCreditScoreAvailable = useAppSelector(selectIsCreditScoreAvailable);
    const showNewInvestIntroWithMockGraph = useAppSelector(selectShowInvestIntroFlag);
    const tabConfig = useAppSelector(selectNavigatorConfig);
    const tabPriority = useAppSelector(selectTabsPriority);
    const { isUnlockedForSpace: isNetWorthUnlockedForSpace } = useShouldShowUpgrade({
        benefitId: 'netWorth',
    });
    const shouldShowEmmaSetUp = useAppSelector(selectShouldShowEmmaSetUpFlow);
    const feedInfo = useBalances(feed.cards.overview);
    const nextStepRef = useRef(undefined);
    const goToNextStepUsingRef = useCallback(() => {
        nextStepRef.current?.();
    }, []);
    const items = useMemo(() => {
        if (showWalkthroughV6) {
            const items = [
                {
                    title: 'Feed',
                    description: 'This is where you’ll find all the information about your linked accounts, analytics, budgeting and recurring payments.',
                    arrowPosition: 'start',
                    bubblePosition: 'top',
                    highlightTab: tabPriority.priority.length ? 'Feed' : undefined,
                    screen: 'Feed',
                },
            ];
            if (feed.cards) {
                items.push({
                    title: 'Get a full view of your assets',
                    description: 'Easily track your total balance and see how your assets are allocated in one simple place.',
                    component: (<CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
              <Overview data={feed.cards.overview}/>
            </CardView>),
                    position: itemPositions?.overview
                        ? {
                            ...itemPositions.overview,
                            y: itemPositions.overview.y - rem(4),
                        }
                        : undefined,
                    arrowPosition: 'center',
                    bubblePosition: 'bottom',
                    screen: 'Feed',
                });
                items.push({
                    title: 'Your everyday accounts',
                    description: 'All your connected accounts can be found under “Everyday” you can manage your accounts and find balances and transactions there.',
                    component: (<CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
              <CardView noMargin>
                <OverviewRow onPress={noop} item={feedInfo[0]} arrowDirection="right"/>
              </CardView>
            </CardView>),
                    position: itemPositions?.overview
                        ? {
                            ...itemPositions.overview,
                            y: itemPositions.overview.y - rem(4),
                        }
                        : undefined,
                    arrowPosition: 'center',
                    bubblePosition: 'bottom',
                    screen: 'Feed',
                });
                items.push({
                    title: 'Watch your money grow',
                    description: 'Monitor your savings, set goals, and build financial security.',
                    component: (<>
              <Spacer h={overViewRowHeight}/>
              <CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
                <CardView noMargin>
                  <OverviewRow onPress={noop} item={feedInfo[1]} arrowDirection="right"/>
                </CardView>
              </CardView>
            </>),
                    position: itemPositions?.overview
                        ? {
                            ...itemPositions.overview,
                            y: itemPositions.overview.y - rem(4),
                        }
                        : undefined,
                    arrowPosition: 'center',
                    bubblePosition: 'bottom',
                    screen: 'Feed',
                });
                items.push({
                    title: 'Build your wealth',
                    description: 'Stay on top of your investments and track your portfolio’s performance.',
                    component: (<>
              <Spacer h={overViewRowHeight}/>
              <Spacer h={overViewRowHeight}/>
              <CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
                <CardView noMargin>
                  <OverviewRow onPress={noop} item={feedInfo[2]} arrowDirection="right"/>
                </CardView>
              </CardView>
            </>),
                    position: itemPositions?.overview
                        ? {
                            ...itemPositions.overview,
                            y: itemPositions.overview.y - rem(4),
                        }
                        : undefined,
                    arrowPosition: 'center',
                    bubblePosition: 'bottom',
                    screen: 'Feed',
                });
                items.push({
                    title: 'Plan for your future',
                    description: 'Get a complete view of your assets, debts, and overall financial health.',
                    component: (<>
              <Spacer h={overViewRowHeight}/>
              <Spacer h={overViewRowHeight}/>
              <Spacer h={overViewRowHeight}/>
              <CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
                <CardView noMargin>
                  <OverviewRow onPress={noop} item={feedInfo[3]} arrowDirection="right"/>
                </CardView>
              </CardView>
            </>),
                    position: itemPositions?.overview
                        ? {
                            ...itemPositions.overview,
                            y: itemPositions.overview.y - rem(4),
                        }
                        : undefined,
                    arrowPosition: 'center',
                    bubblePosition: 'bottom',
                    screen: 'Feed',
                });
                items.push({
                    noContent: true,
                    component: (<CardView noMargin style={styles.highlightCardOverview}>
              <CardView noMargin>
                <OverviewRow item={feedInfo[0]} arrowDirection="right" onPress={goToNextStepUsingRef}/>
              </CardView>
            </CardView>),
                    position: itemPositions?.overview
                        ? {
                            ...itemPositions.overview,
                            height: rem(overViewRowHeight),
                            y: itemPositions.overview.y - rem(4),
                        }
                        : undefined,
                    bubblePosition: 'bottom',
                    pointerPosition: 'bottom',
                    screen: 'Feed',
                    touchHelperLeft: width / 2,
                });
                items.push({
                    title: 'Overview graph',
                    description: 'Here you see how your balance has changed over time. Try tapping and dragging your finger on the graph!',
                    component: (<CardView noMargin style={styles.highlightCardOverviewGraphCard} pointerEvents="none">
              <AccountsHeaderGraph scrollX={scrollX} title="Everyday" blurValues={false} onPressUpgrade={noop} isInTab={false}/>
            </CardView>),
                    position: itemPositions?.accountsOverviewCard
                        ? {
                            ...itemPositions.accountsOverviewCard,
                            y: itemPositions.accountsOverviewCard.y - rem(4),
                        }
                        : undefined,
                    bubblePosition: 'bottom',
                    pointerPosition: 'bottom',
                    screen: 'AccountsOverview',
                    arrowPosition: 'center',
                });
                items.push({
                    title: 'Information icon',
                    description: 'You’ll find this info icon throughout the app. Tapping it will always give you a helpful explanation or more context.',
                    component: (<View style={styles.everydayTitleRow}>
              <Text Text-14 color={INVISIBLE_TEXT_COLOR}>
                Everyday
              </Text>
              <CardView noMargin style={styles.everydayInfoButtonCardView} pointerEvents="none">
                <ToolTip small title={tooltipStrings.Everyday.title} subtitle={tooltipStrings.Everyday.desc}/>
              </CardView>
            </View>),
                    position: itemPositions?.accountsOverviewCard,
                    bubblePosition: 'bottom',
                    pointerPosition: 'bottom',
                    screen: 'AccountsOverview',
                    arrowPosition: 'custom',
                    arrowPositionLeft: rem(104),
                });
                items.push({
                    noContent: true,
                    touchHelperPosition: 'right',
                    touchHelperYOffset: rem(6),
                    component: (<View style={styles.everydayTitleRow}>
              <Spacer w={66}/>
              <CardView noMargin style={styles.everydayInfoButtonCardView}>
                <ToolTip small noBackdrop onModalShown={goToNextStepUsingRef} onModalHidden={goToNextStepUsingRef} title={tooltipStrings.Everyday.title} subtitle={tooltipStrings.Everyday.desc}/>
              </CardView>
            </View>),
                    position: itemPositions?.accountsOverviewCard,
                    bubblePosition: 'bottom',
                    pointerPosition: 'bottom',
                    screen: 'AccountsOverview',
                    touchHelperLeft: rem(104),
                    touchHelperOnPress: () => {
                        goToNextStepUsingRef();
                        Modal.show(<ToolTipContent subtitle={tooltipStrings.Everyday.desc} onPress={() => {
                                Modal.hide(undefined, { top: true });
                            }} btnText={'Okay'}/>, {
                            title: tooltipStrings.Everyday.title,
                            top: true,
                            noBackdrop: true,
                            onDidHideCallback: goToNextStepUsingRef,
                        });
                    },
                });
                items.push({
                    noContent: true,
                    touchHelperPosition: 'none',
                    component: <View />,
                    position: itemPositions?.accountsOverviewCard,
                    bubblePosition: 'bottom',
                    pointerPosition: 'bottom',
                    screen: 'AccountsOverview',
                    cantHandleBack: true,
                });
                items.push({
                    title: 'Add accounts',
                    description: 'You can add new accounts to Emma by clicking this button. The more you add the better Emma can help you.',
                    component: (<View pointerEvents="none">
              <Spacer h={overViewRowHeight}/>
              <Spacer h={overViewRowHeight}/>
              <Spacer h={overViewRowHeight}/>
              <Spacer h={overViewRowHeight}/>
              {removePayTabExperience ? (<WalkthroughOverviewButtonsRow highlight="add"/>) : (<View style={styles.v6OverviewAddButtonContainer}>
                  <Spacer w={12}/>
                  <CardView noMargin style={styles.v6OverviewAddButtonCard}>
                    <OverviewAddButton />
                  </CardView>
                </View>)}
            </View>),
                    position: itemPositions?.overview,
                    arrowPosition: 'custom',
                    bubblePosition: 'bottom',
                    arrowPositionLeft: rem(58),
                    screen: 'Feed',
                });
                if (removePayTabExperience) {
                    items.push({
                        title: 'Pay, request & transfer',
                        description: 'Send money, request payments, or transfer funds anytime right from your Feed',
                        component: (<View pointerEvents="none">
                <Spacer h={overViewRowHeight}/>
                <Spacer h={overViewRowHeight}/>
                <Spacer h={overViewRowHeight}/>
                <Spacer h={overViewRowHeight}/>
                <WalkthroughOverviewButtonsRow highlight="pay"/>
              </View>),
                        position: itemPositions?.overview,
                        arrowPosition: isTablet ? 'custom' : 'center',
                        ...(isTablet && { arrowPositionLeft: rem(150) }),
                        bubblePosition: 'bottom',
                        screen: 'Feed',
                    });
                }
            }
            if (hasAccounts) {
                if (feed.cards.latestTransactions?.length) {
                    items.push({
                        title: 'Recent transactions',
                        description: 'See your latest transactions and quickly identify which bank account they came from.',
                        component: (<CardView noMargin style={styles.highlightCardOverview} pointerEvents="none">
                <Spacer h={8}/>
                <Transactions data={feed.cards.latestTransactions}/>
              </CardView>),
                        arrowPosition: 'center',
                        position: itemPositions?.transactionsSection
                            ? {
                                ...itemPositions.transactionsSection,
                                y: itemPositions.transactionsSection.y - rem(12),
                            }
                            : undefined,
                        bubblePosition: 'bottom',
                    });
                    const txn = feed.cards.latestTransactions[0];
                    items.push({
                        noContent: true,
                        component: (<CardView noMargin style={styles.highlightCardOverview}>
                <CardView noMargin>
                  <RowTransaction item={txn} backgroundDark showUpgradeButton switchingButon={<SwitchingButton data={txn && txn.merchant
                                    ? { merchantInfo: txn.merchant }
                                    : null} guessedHomeCountry={guessedHomeCountry} onOpenCompare={noop}/>} onPress={goToNextStepUsingRef}/>
                </CardView>
              </CardView>),
                        position: itemPositions?.transactionsSection
                            ? {
                                ...itemPositions.transactionsSection,
                                y: itemPositions.transactionsSection.y + rem(40),
                            }
                            : undefined,
                        bubblePosition: 'bottom',
                        pointerPosition: 'bottom',
                        screen: 'Feed',
                        touchHelperLeft: width / 2,
                    });
                    items.push({
                        title: 'Transaction details',
                        description: 'Here’s where you can edit the category, and add tags, notes, or a receipt. You can also share this transaction with friends.',
                        component: <WalkthroughTransactionDetails id={txn.id}/>,
                        position: itemPositions?.transactionScreen,
                        bubblePosition: 'bottom',
                        pointerPosition: 'bottom',
                        arrowPosition: 'center',
                        screen: 'Item',
                    });
                }
                items.push({
                    title: 'Budgeting',
                    description: hasBudget
                        ? 'The chart shows how much money you are allowed to spend to stay on track. By clicking on the card you will see and be able to edit your total budget as well as setting category, merchant and rolling budgets!'
                        : 'This section will help you set up a total budget and individual category budgets. It’s important you set them to keep track of your spending',
                    component: (<View pointerEvents="none" style={styles.highlightCardAnalyticsBudgetingv6}>
              <IncomeSpendingCard style={styles.highlightCardHidden} disabled/>
              <BudgetingCardContainer style={styles.highlightCardShown}/>
            </View>),
                    arrowPosition: 'end',
                    position: itemPositions?.analytics,
                    bubblePosition: 'top',
                    screen: 'Feed',
                });
                items.push({
                    title: 'Analytics',
                    description: 'This is your monthly income versus spending. You can see more details by clicking on the “this month” card. ',
                    component: (<View pointerEvents="none" style={styles.highlightCardAnalyticsBudgetingv6}>
              <IncomeSpendingCard style={styles.highlightCardShown}/>
              <BudgetingCardContainer style={styles.highlightCardHidden} disabled/>
            </View>),
                    arrowPosition: 'start',
                    position: itemPositions?.analytics,
                    bubblePosition: 'top',
                    screen: 'Feed',
                });
                items.push({
                    noContent: true,
                    touchHelperPosition: 'right',
                    touchHelperYOffset: -rem(26),
                    component: (<View style={styles.highlightCardAnalyticsBudgeting}>
              <IncomeSpendingCard style={styles.highlightCardShown} onPress={goToNextStepUsingRef}/>
              <BudgetingCardContainer style={styles.highlightCardHidden} disabled/>
            </View>),
                    position: itemPositions?.analytics,
                    bubblePosition: 'bottom',
                    pointerPosition: 'bottom',
                    touchHelperLeft: width / 2 - rem(64),
                    screen: 'Feed',
                });
                items.push({
                    noContent: true,
                    component: (<WalkthroughAnalyticsChartSettingsBtn itemPosition={itemPositions?.analyticsChartSettings} onNext={goToNextStepUsingRef}/>),
                    arrowPosition: 'center',
                    position: itemPositions?.analyticsChartSettings,
                    bubblePosition: 'bottom',
                    screen: 'Analytics',
                    touchHelperLeft: (itemPositions?.analyticsChartSettings?.x || 0) + rem(20),
                    touchHelperYOffset: rem(10),
                    touchHelperOnPress: () => {
                        goToNextStepUsingRef();
                        PickerModal.show({
                            pickerOptions: CHART_TYPE_OPTIONS_WALKTHROUGH,
                            selectedIconFill: colors.elements.brand,
                            selectedValue: CHART_TYPE_OPTIONS.find((item) => item.value === chartType),
                            onSelected: (selected) => {
                                dispatch(setChartType(selected.value));
                                goToNextStepUsingRef();
                            },
                            overlay: (<View pointerEvents="none" style={styles.pickerOptionOverlay}>
                    <TouchHelper />
                  </View>),
                        }, {
                            noBackdrop: true,
                            title: 'Chart type',
                            nonDismissable: true,
                        }, undefined);
                    },
                });
                items.push({
                    noContent: true,
                    touchHelperPosition: 'none',
                    component: <View />,
                    position: itemPositions?.analyticsChartSettings,
                    bubblePosition: 'bottom',
                    pointerPosition: 'bottom',
                    screen: 'Analytics',
                    cantHandleBack: true,
                });
                items.push({
                    title: 'Spend over time',
                    description: 'Use this chart to easily compare your spending in the current period versus the previous period.',
                    component: (<WalkthroughIncomeSpendingCard itemPosition={itemPositions?.analyticsChart}/>),
                    arrowPosition: 'center',
                    position: itemPositions?.analyticsChart,
                    bubblePosition: 'bottom',
                    screen: 'Analytics',
                });
                items.push({
                    title: 'Recurring payments',
                    description: 'Here are all your bills, subscriptions and other periodic payments. You can see payment history, edit and create new recurring payments by clicking “See all”.',
                    component: (<CardView noMargin withPadding pointerEvents="none" style={styles.highlightCardOverview}>
              <Spacer h={8}/>
              <Subscriptions />
            </CardView>),
                    arrowPosition: 'center',
                    position: itemPositions?.subscriptions
                        ? {
                            ...itemPositions.subscriptions,
                            y: itemPositions.subscriptions.y - rem(12),
                        }
                        : undefined,
                    bubblePosition: 'bottom',
                    screen: 'Feed',
                });
                if (removePayTabExperience) {
                    items.push({
                        title: 'Split bills with friends',
                        description: 'Create or join a group right here to start tracking, splitting, and settling shared expenses',
                        component: (<CardView noMargin withPadding pointerEvents="none" style={styles.highlightCardOverview}>
                <Spacer h={8}/>
                <FeedGroupsSection noMarginBottom/>
              </CardView>),
                        arrowPosition: 'center',
                        position: itemPositions?.groups
                            ? {
                                ...itemPositions.groups,
                                y: itemPositions.groups.y - rem(12),
                            }
                            : undefined,
                        bubblePosition: 'top',
                        screen: 'Feed',
                    });
                }
                if (subscriptions?.length) {
                    const subscription = subscriptions[0];
                    items.push({
                        noContent: true,
                        component: (<CardView noMargin withPadding style={styles.highlightCardOverview}>
                <CardView noMargin>
                  <RowSubscription data={subscription} onPress={goToNextStepUsingRef} switchingButton={<SwitchingButton data={subscription} guessedHomeCountry={guessedHomeCountry} onOpenCompare={noop}/>} blurValues={false}/>
                </CardView>
              </CardView>),
                        position: itemPositions?.subscriptions
                            ? {
                                ...itemPositions.subscriptions,
                                y: itemPositions.subscriptions.y + rem(36),
                            }
                            : undefined,
                        arrowPosition: 'center',
                        bubblePosition: 'bottom',
                        pointerPosition: 'bottom',
                        touchHelperLeft: width / 2,
                        screen: 'Feed',
                    });
                    items.push({
                        title: 'Manage your subscription!',
                        description: 'Edit the payment, view the history of past and upcoming payments, and track any changes in the amount.',
                        component: (<WalkthroughSubscriptionScreenItem subscription={subscription}/>),
                        position: itemPositions?.subscriptions
                            ? {
                                ...itemPositions.subscriptions,
                                y: itemPositions.subscriptions.y + rem(36),
                            }
                            : undefined,
                        arrowPosition: 'center',
                        bubblePosition: 'bottom',
                        screen: 'Subscription',
                    });
                }
            }
            items.push({
                title: 'Create separate spaces',
                description: 'Set up individual areas to manage family or business finances. Invite others to your Space to collaborate and share financial oversight.',
                component: <View />,
                navHeaderHighlightContent: 'spaces',
                position: undefined,
                arrowPosition: 'custom',
                bubblePosition: 'bottom',
                arrowPositionLeft: contentWidth - rem(74),
                screen: 'Feed',
            });
            const showSaveItems = potsAccountStatus === 'NOT_STARTED' &&
                maxInterestRate &&
                tabPriority.priority.includes('save');
            if (showSaveItems) {
                items.push({
                    noContent: true,
                    component: <View />,
                    position: undefined,
                    bubblePosition: 'bottom',
                    touchHelperPosition: 'none',
                    bottomTabTouchHelperPosition: 'right',
                    highlightTab: tabPriority.priority.length ? 'Save' : undefined,
                    screen: 'Feed',
                });
                items.push({
                    title: 'Save and earn',
                    description: `Grow your savings by earning ${maxInterestRate}% in interest. Earn up to 30% in cashback and discover saving opportunities. All in one place.`,
                    component: <WalkthroughSaveTabItem />,
                    arrowPosition: 'center',
                    position: undefined,
                    bubblePosition: 'bottom',
                    screen: 'Save',
                });
            }
            const shouldShowPayItems = tabPriority.priority.includes('pay');
            if (shouldShowPayItems) {
                items.push({
                    title: 'Pay and make transfers',
                    description: 'Emma Pay allows you to send and request money without remembering bank details. You can also manage shared spending with our Groups feature.',
                    arrowPosition: 'start',
                    bubblePosition: 'top',
                    highlightTab: 'Pay',
                    screen: !showSaveItems ? 'Feed' : 'Save',
                });
                items.push({
                    noContent: true,
                    component: <View />,
                    position: undefined,
                    bubblePosition: 'bottom',
                    touchHelperPosition: 'none',
                    bottomTabTouchHelperPosition: 'right',
                    highlightTab: 'Pay',
                    screen: !showSaveItems ? 'Feed' : 'Save',
                });
                items.push({
                    title: 'Pay, request, and transfer money with ease',
                    description: 'Pay bills, request money, and transfer funds quickly and securely.',
                    component: (<CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
              <PayTabQuickCTAs cardViewStyle={styles.payTabQuickCTAsCardViewStyles}/>
            </CardView>),
                    arrowPosition: 'center',
                    position: itemPositions?.payTabQuickCTAs
                        ? {
                            ...itemPositions.payTabQuickCTAs,
                            y: itemPositions.payTabQuickCTAs.y + rem(12),
                        }
                        : undefined,
                    bubblePosition: 'bottom',
                    screen: 'Pay',
                });
                items.push({
                    title: 'Track money owed',
                    description: 'This area shows your current balance of money owed to you by friends and money you owe them. You can also pay or request money by tapping here.',
                    component: (<CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
              <CreditAndDebtSection cardViewStyle={styles.creditDebtSection}/>
            </CardView>),
                    arrowPosition: 'center',
                    position: itemPositions?.creditDebtSection
                        ? {
                            ...itemPositions.creditDebtSection,
                            y: itemPositions.creditDebtSection.y + rem(12),
                        }
                        : undefined,
                    bubblePosition: 'bottom',
                    screen: 'Pay',
                });
                items.push({
                    title: 'Manage group expenses',
                    description: 'Track shared expenses and split costs effortlessly with Emma’s group feature.',
                    component: (<CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
              <GroupsSection cardViewStyle={styles.groupsSectionCardViewStyles} titleContainerStyle={styles.groupsSectionTitleContainerStyle}/>
            </CardView>),
                    arrowPosition: 'center',
                    position: itemPositions?.groupsSection
                        ? {
                            ...itemPositions.groupsSection,
                            y: itemPositions.groupsSection.y - rem(4),
                        }
                        : undefined,
                    bubblePosition: 'bottom',
                    screen: 'Pay',
                });
            }
            const showInvestItems = showSecclFlow &&
                !showNewInvestIntroWithMockGraph &&
                shouldShowInvestItemsInWalkthroughV6 &&
                tabPriority.priority.includes('invest');
            // where the walkthrough parks while highlighting the next tab; the pay
            // tab may be absent in the remove-pay-tab experience
            const payScreenOrFallback = shouldShowPayItems
                ? 'Pay'
                : (showSaveItems && 'Save') || 'Feed';
            if (showInvestItems) {
                items.push({
                    noContent: true,
                    component: <View />,
                    position: undefined,
                    bubblePosition: 'bottom',
                    touchHelperPosition: 'none',
                    bottomTabTouchHelperPosition: 'left',
                    highlightTab: tabPriority.priority.length ? 'Invest' : undefined,
                    screen: payScreenOrFallback,
                });
                items.push({
                    title: 'Start investing',
                    description: 'With Emma Invest, you can trade more than 25+ ETFs & funds with as little as £1. Remember your capital is at risk if you invest.',
                    component: (<WalkthroughInvestTabItem y={itemPositions?.investTabInfo?.y} height={itemPositions?.investTabInfo?.height}/>),
                    arrowPosition: 'center',
                    position: undefined,
                    bubblePosition: 'bottom',
                    screen: 'Invest',
                });
            }
            const shouldShowBorrowItems = tabPriority.priority.includes('borrow');
            if (shouldShowBorrowItems) {
                items.push({
                    title: isCreditScoreAvailable
                        ? 'Grow your credit score'
                        : 'Borrow and grow credit score',
                    description: isCreditScoreAvailable
                        ? 'Track your score with ease and get personalised tips to help it grow. You can even report your rent to the UK’s top credit agencies to boost your history.'
                        : 'This tab is helpful to start reporting your rent payments to the top 3 credit agencies to boost your credit history. You can also search for loans and credit cards.',
                    arrowPosition: 'start',
                    bubblePosition: 'top',
                    highlightTab: tabPriority.priority.length ? 'Borrow' : undefined,
                    screen: showInvestItems ? 'Invest' : payScreenOrFallback,
                });
                const showRentReportingItems = hasRentReporting && !rentReportingData?.current;
                if (showRentReportingItems) {
                    items.push({
                        noContent: true,
                        component: <View />,
                        position: undefined,
                        bubblePosition: 'bottom',
                        touchHelperPosition: 'none',
                        bottomTabTouchHelperPosition: 'left',
                        highlightTab: tabPriority.priority.length ? 'Borrow' : undefined,
                        screen: 'Feed',
                    });
                    items.push({
                        title: 'Rent reporting',
                        description: 'Use Emma to report your rent payments and improve your credit score effortlessly.',
                        component: (<CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
                <Spacer h={8}/>
                <RentReportingSection cardViewStyle={styles.groupsSectionCardViewStyles} titleContainerStyle={styles.groupsSectionTitleContainerStyle}/>
              </CardView>),
                        arrowPosition: 'center',
                        position: itemPositions?.rentReporting
                            ? {
                                ...itemPositions.rentReporting,
                                y: itemPositions.rentReporting.y - rem(12),
                            }
                            : undefined,
                        bubblePosition: 'top',
                        screen: 'Borrow',
                    });
                }
            }
            items.push({
                title: 'Settings',
                description: 'Tap your profile icon from anywhere in the app to quickly access your settings.',
                component: <View />,
                navHeaderHighlightContent: 'profile',
                position: undefined,
                arrowPosition: 'custom',
                bubblePosition: 'bottom',
                arrowPositionLeft: rem(28),
                screen: shouldShowBorrowItems ? 'Borrow' : 'Feed',
            });
            items.push({
                noContent: true,
                component: <View />,
                withAction: true,
                position: undefined,
                bubblePosition: 'bottom',
                touchHelperPosition: 'none',
                navHeaderHighlightContent: 'profile',
                screen: shouldShowBorrowItems ? 'Borrow' : 'Feed',
            });
            items.push({
                title: 'Quests to master Emma!',
                description: 'Use Quests to discover and try out many of the powerful features available in Emma. Let’s check them out!',
                component: (<CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
            <Quests key="Quests" style={styles.noMarginHorizontal}/>
          </CardView>),
                arrowPosition: 'center',
                position: itemPositions?.quests
                    ? {
                        ...itemPositions.quests,
                        y: itemPositions.quests.y + rem(4),
                    }
                    : undefined,
                bubblePosition: 'top',
                screen: 'AccountSettings',
            });
            items.push({
                title: 'Learning materials',
                description: 'Explore articles and guides to grow your financial knowledge and discover ways to use Emma.',
                component: (<View style={styles.row} pointerEvents="none">
            <View style={styles.flex}/>
            <View style={styles.flex}>
              <CardView noMargin style={styles.learnCardContainer}>
                <LearnCard style={styles.learnCard}/>
              </CardView>
            </View>
          </View>),
                arrowPosition: 'end',
                position: itemPositions?.learn
                    ? {
                        ...itemPositions.learn,
                        y: itemPositions.learn.y + rem(12),
                    }
                    : undefined,
                bubblePosition: 'bottom',
                screen: 'AccountSettings',
            });
            items.push({
                noContent: true,
                component: (<View style={styles.row}>
            <View style={styles.flex}/>
            <View style={styles.flex}>
              <CardView noMargin style={styles.learnCardContainer}>
                <LearnCard style={styles.learnCard} onPress={goToNextStepUsingRef}/>
              </CardView>
            </View>
          </View>),
                arrowPosition: 'center',
                bubblePosition: 'bottom',
                screen: 'AccountSettings',
                touchHelperLeft: width / 2 + rem(50),
                touchHelperYOffset: rem(20),
                position: itemPositions?.learn
                    ? {
                        ...itemPositions.learn,
                        y: itemPositions.learn.y + rem(12),
                    }
                    : undefined,
            });
            items.push({
                title: 'Tutorial anytime!',
                description: 'You can always restart this walkthrough from here. See you soon 😉',
                component: (<>
            <Spacer h={12}/>
            <CardView noMargin pointerEvents="none" style={styles.highlightCardOverview}>
              <LearnWalkthroughCard data={emmaWalkthroughData} onPress={noop}/>
            </CardView>
          </>),
                arrowPosition: 'center',
                position: undefined,
                bubblePosition: 'bottom',
                screen: 'Learn',
            });
            return items;
        }
        const investItem = {
            title: 'Start investing',
            description: isSecclEnabled
                ? 'Discover 25+ ETFs & funds and start your investment journey. Capital at risk.'
                : 'With Emma Invest, you can trade more than 2,000 international stocks with as little as £1. Remember your capital is at risk if you invest.',
            arrowPosition: 'start',
            bubblePosition: 'top',
            highlightTab: tabConfig.showInvestTab && tabPriority.priority.includes('invest')
                ? 'Invest'
                : undefined,
        };
        const saveItem = {
            title: 'Save and earn',
            description: `${guessedHomeCountry === 'GB' && maxInterestRate
                ? `Grow your savings by earning ${maxInterestRate}% in interest${maxInterestProduct?.interestPaymentFrequency
                    ? `, paid ${maxInterestProduct.interestPaymentFrequency.toLowerCase()}. `
                    : '.'}`
                : ''}Earn up to 30% in cashback and discover saving opportunities. All in one place.`,
            arrowPosition: 'start',
            bubblePosition: 'top',
            highlightTab: tabConfig.showSaveTab && tabPriority.priority.includes('save')
                ? 'Save'
                : undefined,
        };
        const v2Items = [
            {
                title: 'Feed',
                description: 'This is where you’ll find all the information about your linked accounts, analytics, budgeting and recurring payments.',
                arrowPosition: 'start',
                bubblePosition: 'top',
                highlightTab: tabPriority.priority.length ? 'Feed' : undefined,
            },
            {
                title: 'Your everyday accounts',
                description: 'All your connected accounts can be found under “Everyday” you can manage your accounts and find balances and transactions there.',
                component: (<CardView noMargin style={styles.highlightCardEveryday}>
            <TouchableBounce onPress={() => {
                        navigation.navigateDeprecated('AccountsOverview', {
                            title: 'Everyday',
                        });
                    }}>
              <OverviewText style={styles.overviewText} title="Everyday" arrowDirection="right"/>
            </TouchableBounce>
          </CardView>),
                position: itemPositions?.overview,
                arrowPosition: 'start',
                bubblePosition: 'bottom',
            },
            {
                title: 'Add accounts',
                description: 'You can add new accounts to Emma by clicking this button. The more you add the better Emma can help you.',
                component: removePayTabExperience ? (<View style={styles.legacyOverviewButtonsRowOffset}>
            <WalkthroughOverviewButtonsRow highlight="add"/>
          </View>) : (<View style={[
                        styles.highlightCardAddAccount,
                        !isNetWorthUnlockedForSpace &&
                            styles.highlightCardAddAccountTrack,
                    ]}>
            <OverviewAddButton />
          </View>),
                position: itemPositions?.overview,
                arrowPosition: removePayTabExperience ? 'custom' : 'start',
                ...(removePayTabExperience && { arrowPositionLeft: rem(58) }),
                bubblePosition: 'bottom',
            },
            ...(removePayTabExperience
                ? [
                    {
                        title: 'Pay, request & transfer',
                        description: 'Send money, request payments, or transfer funds anytime right from your Feed',
                        component: (<View style={styles.legacyOverviewButtonsRowOffset}>
                  <WalkthroughOverviewButtonsRow highlight="pay"/>
                </View>),
                        position: itemPositions?.overview,
                        arrowPosition: isTablet ? 'custom' : 'center',
                        ...(isTablet && { arrowPositionLeft: rem(150) }),
                        bubblePosition: 'bottom',
                    },
                ]
                : []),
            ...(hasAccounts
                ? [
                    {
                        title: 'Your transactions',
                        description: 'Click “See all” to see all your transactions. You can also search for a specific transaction by clicking “search” icon on the top right of the screen.',
                        component: (<CardView style={[
                                styles.highlightCardSeeAll,
                                styles.highlightCardSeeAllTop,
                            ]} noMargin>
                  <TransactionsSeeAllButton />
                </CardView>),
                        arrowPosition: 'end',
                        position: itemPositions?.transactionsSection,
                        bubblePosition: 'bottom',
                    },
                    {
                        title: 'Budgeting',
                        // eslint-disable-next-line no-nested-ternary
                        description: hasBudget
                            ? didSetupBudgetInOnboarding && !allowSkipWalkthrough
                                ? 'We’ve set up a budget based on your past spending. You can track your spending or edit your limits in this section.'
                                : 'The chart shows how much money you are allowed to spend to stay on track. By clicking on the card you will see and be able to edit your total budget as well as setting category, merchant and rolling budgets!'
                            : 'This section will help you set up a total budget and individual category budgets. It’s important you set them to keep track of your spending',
                        component: (<View style={styles.highlightCardAnalyticsBudgeting}>
                  <IncomeSpendingCard style={styles.highlightCardHidden} disabled/>
                  <BudgetingCardContainer style={styles.highlightCardShown}/>
                </View>),
                        arrowPosition: 'end',
                        position: itemPositions?.analytics,
                        bubblePosition: 'bottom',
                    },
                    {
                        title: 'Analytics',
                        description: 'This is your monthly income versus spending. You can see more details by clicking on the “this month” card. ',
                        component: (<View style={styles.highlightCardAnalyticsBudgeting}>
                  <IncomeSpendingCard style={styles.highlightCardShown}/>
                  <BudgetingCardContainer style={styles.highlightCardHidden} disabled/>
                </View>),
                        arrowPosition: 'start',
                        position: itemPositions?.analytics,
                        bubblePosition: 'bottom',
                    },
                    {
                        title: 'Recurring payments',
                        description: 'Here are all your bills, subscriptions and other periodic payments. You can see payment history, edit and create new recurring payments.',
                        component: (<CardView noMargin withPadding pointerEvents="none" style={styles.highlightCardOverview}>
                  <Spacer h={8}/>
                  <Subscriptions />
                </CardView>),
                        arrowPosition: 'center',
                        position: itemPositions?.subscriptions
                            ? {
                                ...itemPositions.subscriptions,
                                y: itemPositions.subscriptions.y - rem(12),
                            }
                            : undefined,
                        bubblePosition: 'bottom',
                        screen: 'Feed',
                    },
                    ...(removePayTabExperience
                        ? [
                            {
                                title: 'Split bills with friends',
                                description: 'Create or join a group right here to start tracking, splitting, and settling shared expenses',
                                component: (<CardView noMargin withPadding pointerEvents="none" style={styles.highlightCardOverview}>
                        <Spacer h={8}/>
                        <FeedGroupsSection noMarginBottom/>
                      </CardView>),
                                arrowPosition: 'center',
                                position: itemPositions?.groups
                                    ? {
                                        ...itemPositions.groups,
                                        y: itemPositions.groups.y - rem(12),
                                    }
                                    : undefined,
                                bubblePosition: 'top',
                            },
                        ]
                        : []),
                ]
                : []),
            saveItem,
            {
                title: 'Pay and make transfers',
                description: 'Emma Pay allows you to send and request money without remembering bank details. You can also manage shared spending with our Groups feature.',
                arrowPosition: 'start',
                bubblePosition: 'top',
                highlightTab: tabConfig.showPayTab && tabPriority.priority.includes('pay')
                    ? 'Pay'
                    : undefined,
            },
            {
                title: 'Networth',
                description: 'Visualize your net worth growth and see the bigger picture of your financial journey. Understand how your assets and debts are impacting your overall wealth.',
                arrowPosition: 'start',
                bubblePosition: 'top',
                highlightTab: tabConfig.showNetworthTab && tabPriority.priority.includes('networth')
                    ? 'NetworthTab'
                    : undefined,
            },
            {
                title: 'Budgeting',
                description: 'You can quickly access your budgeting from this tab.',
                arrowPosition: 'start',
                bubblePosition: 'top',
                highlightTab: tabConfig.showBudgetingTab &&
                    tabPriority.priority.includes('budgeting')
                    ? 'BudgetingTab'
                    : undefined,
            },
            {
                title: 'Recurring payments',
                description: 'You can quickly access your recurring payments from this tab.',
                arrowPosition: 'start',
                bubblePosition: 'top',
                highlightTab: tabConfig.showRecurringTab &&
                    tabPriority.priority.includes('recurring')
                    ? 'RecurringTab'
                    : undefined,
            },
            {
                title: 'Manage group expenses',
                description: 'Track shared expenses and split costs effortlessly with Emma’s group feature.',
                arrowPosition: 'start',
                bubblePosition: 'top',
                highlightTab: tabConfig.showGroupTab && tabPriority.priority.includes('groups')
                    ? 'GroupTab'
                    : undefined,
            },
            investItem,
            {
                title: isCreditScoreAvailable
                    ? 'Grow your credit score'
                    : 'Borrow and grow credit score',
                description: isCreditScoreAvailable
                    ? 'Track your score with ease and get personalised tips to help it grow. You can even report your rent to the UK’s top credit agencies to boost your history.'
                    : 'This tab is helpful to start reporting your rent payments to the top 3 credit agencies to boost your credit history. You can also search for loans and credit cards.',
                arrowPosition: 'start',
                bubblePosition: 'top',
                highlightTab: tabConfig.showBorrowTab && tabPriority.priority.includes('borrow')
                    ? 'Borrow'
                    : undefined,
            },
        ];
        const result = v2Items.filter((item) => item.position?.y || item.highlightTab);
        return result;
    }, [
        showWalkthroughV6,
        isSecclEnabled,
        tabConfig.showInvestTab,
        tabConfig.showSaveTab,
        tabConfig.showPayTab,
        tabConfig.showNetworthTab,
        tabConfig.showBudgetingTab,
        tabConfig.showRecurringTab,
        tabConfig.showGroupTab,
        tabConfig.showBorrowTab,
        tabPriority.priority,
        guessedHomeCountry,
        maxInterestRate,
        maxInterestProduct?.interestPaymentFrequency,
        styles.highlightCardEveryday,
        styles.overviewText,
        styles.highlightCardAddAccount,
        styles.highlightCardAddAccountTrack,
        styles.highlightCardSeeAll,
        styles.highlightCardSeeAllTop,
        styles.highlightCardAnalyticsBudgeting,
        styles.highlightCardHidden,
        styles.highlightCardShown,
        styles.highlightCardAnalyticsBudgetingv6,
        styles.highlightCardOverview,
        styles.noMarginHorizontal,
        styles.row,
        styles.flex,
        styles.learnCardContainer,
        styles.learnCard,
        styles.highlightCardOverviewGraphCard,
        styles.everydayTitleRow,
        styles.everydayInfoButtonCardView,
        styles.v6OverviewAddButtonContainer,
        styles.v6OverviewAddButtonCard,
        styles.pickerOptionOverlay,
        styles.payTabQuickCTAsCardViewStyles,
        styles.legacyOverviewButtonsRowOffset,
        styles.creditDebtSection,
        isTablet,
        styles.groupsSectionCardViewStyles,
        styles.groupsSectionTitleContainerStyle,
        itemPositions.overview,
        itemPositions?.analytics,
        itemPositions.subscriptions,
        itemPositions?.analyticsChartSettings,
        itemPositions?.analyticsChart,
        itemPositions.quests,
        itemPositions.learn,
        itemPositions.accountsOverviewCard,
        itemPositions.transactionsSection,
        itemPositions?.transactionScreen,
        itemPositions.payTabQuickCTAs,
        itemPositions.creditDebtSection,
        itemPositions.groupsSection,
        itemPositions.groups,
        removePayTabExperience,
        itemPositions?.investTabInfo?.y,
        itemPositions?.investTabInfo?.height,
        itemPositions.rentReporting,
        isNetWorthUnlockedForSpace,
        hasAccounts,
        hasBudget,
        feed.cards,
        goToNextStepUsingRef,
        width,
        subscriptions,
        contentWidth,
        potsAccountStatus,
        showSecclFlow,
        shouldShowInvestItemsInWalkthroughV6,
        feedInfo,
        scrollX,
        didSetupBudgetInOnboarding,
        allowSkipWalkthrough,
        colors.elements.brand,
        chartType,
        hasRentReporting,
        rentReportingData,
        showNewInvestIntroWithMockGraph,
        isCreditScoreAvailable,
    ]);
    const currentStep = useAppSelector((store) => store.feed.walkthrough?.step || 0);
    const item = items.at(currentStep);
    const isMounted = useRef(false);
    useFocusEffect(() => {
        if (isMounted.current) {
            if (showWalkthroughV6) {
                if (item?.screen && item?.screen !== routeName) {
                    if (item?.screen === 'Save') {
                        navigateToTab('Save', { tab: 'pots' });
                    }
                    else if (item?.screen === 'Item') {
                        const txn = feed.cards.latestTransactions?.[0];
                        if (txn) {
                            navigation.navigateDeprecated('Item', { id: txn.id });
                        }
                    }
                    else if (item?.screen === 'Subscription') {
                        const subscription = subscriptions?.[0];
                        if (subscription) {
                            navigation.navigateDeprecated('Subscription', {
                                id: subscription.id,
                            });
                        }
                    }
                    else {
                        navigation.navigateDeprecated(item.screen);
                    }
                }
            }
        }
        else {
            isMounted.current = true;
        }
    });
    useFocusEffect(() => {
        if (item) {
            scrollRef?.current?.scrollTo({
                y: (item.position?.y || 0) - (item.bubblePosition === 'top' ? 220 : 45),
                x: 0,
                animated: true,
            });
        }
    });
    useEffect(() => {
        if (item === undefined) {
            dispatch(setWalkthroughStatus('complete'));
        }
    }, [item]);
    useEffect(() => {
        if (item?.screen) {
            navigation.setParams({
                headerTintColor: colors.elements.primaryOnModal,
            });
        }
    }, [item?.screen, colors.elements.primaryOnModal]);
    useEffect(() => () => {
        // the screen may already have been removed (e.g. Done pops back to Feed),
        // and SET_PARAMS on a removed route is an unhandled action
        if (navigation.getState()?.routes.some((r) => r.key === routeKey)) {
            navigation.setParams({
                headerTintColor: colors.elements.primary,
            });
        }
    }, [colors.elements.primary, routeKey]);
    const highlightTabIndex = useMemo(() => item?.highlightTab !== undefined
        ? navigation
            .getState()
            .routeNames.findIndex((r) => r === item?.highlightTab)
        : -1, [item?.highlightTab]);
    const goToStep = useCallback((step) => {
        const nextStep = items.at(step);
        if (nextStep) {
            if (nextStep.tab === item?.tab && !showWalkthroughV6) {
                if (Platform.OS !== 'android') {
                    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
                }
            }
            dispatch(setWalkthroughStep({
                index: step,
                highlightTabIndex: navigation
                    .getState()
                    .routeNames.findIndex((item) => item === nextStep.highlightTab),
                navHeaderHighlightContent: nextStep.navHeaderHighlightContent,
                bottomTabTouchHelperPosition: nextStep.bottomTabTouchHelperPosition,
                count: items.length,
                withAction: nextStep.withAction,
            }));
            if (nextStep.screen && item?.screen) {
                const isSameScreen = nextStep?.screen === item?.screen;
                if (isSameScreen) {
                    if (nextStep.tab === item?.tab) {
                        if (Platform.OS !== 'android') {
                            LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
                        }
                    }
                    scrollRef?.current?.scrollTo({
                        y: (nextStep.position?.y || 0) -
                            (nextStep.bubblePosition === 'top' ? 220 : 45),
                        x: 0,
                        animated: true,
                    });
                }
                else if (nextStep.screen === 'Save') {
                    navigateToTab('Save', { tab: 'pots' });
                }
                else if (nextStep.screen === 'Item') {
                    const txn = feed.cards.latestTransactions?.[0];
                    if (txn) {
                        navigation.navigate('Item', { id: txn.id });
                    }
                }
                else if (nextStep.screen === 'Subscription') {
                    const subscription = subscriptions?.[0];
                    if (subscription) {
                        navigation.navigate('Subscription', { id: subscription.id });
                    }
                }
                else {
                    navigation.navigateDeprecated(nextStep.screen);
                }
            }
        }
    }, [
        feed.cards.latestTransactions,
        item?.screen,
        item?.tab,
        items,
        navigateToTab,
        scrollRef,
        showWalkthroughV6,
        subscriptions,
    ]);
    const nextStep = useCallback(() => {
        goToStep(currentStep + 1);
    }, [currentStep, goToStep]);
    useEffect(() => {
        nextStepRef.current = nextStep;
    }, [nextStep]);
    const previousStep = useCallback(() => {
        const previousStep = items?.[currentStep - 1];
        goToStep(previousStep?.cantHandleBack ? currentStep - 2 : currentStep - 1);
    }, [currentStep, goToStep, items]);
    const onPressReset = useCallback(() => {
        Amplitude.logEvent('Restart Walkthrough');
        navigation.navigateDeprecated('Feed');
        dispatch(setWalkthroughStep({
            index: 0,
            highlightTabIndex: 0,
            count: items.length,
        }));
        scrollRef?.current?.scrollTo({
            y: 0,
            x: 0,
            animated: true,
        });
    }, [items.length, scrollRef]);
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const isFloPaywallC1 = useAppSelector((store) => selectFloPaywallC1Inside(store, false));
    const onPressDone = useCallback(() => {
        Amplitude.logEvent('Completed Walkthrough');
        if (Platform.OS !== 'android') {
            LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        }
        dispatch(putWalkthroughComplete(new Date()));
        dispatch(setWalkthroughStatus('complete'));
        scrollRef?.current?.scrollTo({ x: 0, y: 0, animated: true });
        const flagValue = selectFeatureFlags(store.getState())
            .premium_on_walkthrough_ended?.value;
        const isUserFreeTier = selectIsUserFreeTier(store.getState());
        const isGBUser = store.getState().user.user.guessedHomeCountry === 'GB';
        navigation.navigate('Feed');
        if (store.getState().setUpEmma.showEmmaSteps && shouldShowEmmaSetUp) {
            navigation.navigate('SetUpEmmaSteps');
            Amplitude.logEvent('Open Setup', {
                type: 'basic',
                source: 'walkthrough',
            });
            return;
        }
        if (!flagValue || flagValue === 'no' || !isUserFreeTier) {
            if (hasWalkthroughEndStepToShow(completeSetupStepsSelector(store.getState()), isGBUser)) {
                navigation.navigateDeprecated('WalkthroughEnded', { completed: true });
            }
        }
        else if (flagValue === 'blinkist') {
            navigation.navigateDeprecated('EmmaProModal', {});
        }
        else if (flagValue === 'choose_your_plan') {
            navigation.navigateDeprecated('SelectPremiumTierScreen', {
                showChecklistView: true,
                showClose: true,
                popCount: 1,
                isFloPaywallC1,
            });
        }
    }, [isFloPaywallC1, scrollRef, shouldShowEmmaSetUp]);
    const tabCount = tabPriority.priority.length + 1;
    const selectingSidebar = isTablet && item?.highlightTab;
    const containerStyle = useMemo(() => {
        if (!item)
            return undefined;
        if (selectingSidebar) {
            return {
                top: rem(72) * (highlightTabIndex || 0) +
                    (isFixedPosition ? TAB_SCREEN_HEADER_HEIGHT + rem(32) : 0),
            };
        }
        return item.bubblePosition === 'top'
            ? {
                bottom: item.highlightTab
                    ? scrollHeight - (item.overrideHeight || scrollLayoutHeight || 0)
                    : scrollHeight -
                        (item.overrideHeight || item.position?.height || 0) -
                        (item.position?.y || 0),
            }
            : {
                top: item.position?.y || 0,
            };
    }, [
        highlightTabIndex,
        isFixedPosition,
        item,
        scrollHeight,
        scrollLayoutHeight,
        selectingSidebar,
    ]);
    if (!item || !containerStyle) {
        return null;
    }
    const itemWidth = width / tabCount;
    const leftPositionOfItem = itemWidth * (highlightTabIndex !== undefined ? highlightTabIndex : 0) +
        itemWidth / 2 -
        BUBBLE_ARROW_WIDTH / 2;
    const arrow = (<WalkthroughBubbleArrow style={[
            item.arrowPosition === 'start' && styles.bubbleArrowStart,
            item.arrowPosition === 'end' && styles.bubbleArrowEnd,
            item.arrowPosition === 'cardButtonLeft' &&
                styles.bubbleArrowCardButtonLeft,
            item.arrowPosition === 'cardButtonRight' &&
                styles.bubbleArrowCardButtonRight,
            item.arrowPosition === 'center' && styles.bubbleArrowCenter,
            item.bubblePosition === 'top'
                ? styles.bubbleArrowTop
                : styles.bubbleArrowBottom,
            selectingSidebar && styles.bubbleArrowLeft,
            item.arrowPosition === 'custom' && {
                marginLeft: item.arrowPositionLeft,
            },
            highlightTabIndex !== undefined &&
                highlightTabIndex > -1 && { marginLeft: leftPositionOfItem },
        ]}/>);
    return (<View style={styles.container}>
      <WalkthroughOverlay style={styles.overlay}/>
      <View style={[
            styles.itemContainer,
            selectingSidebar
                ? { ...styles.itemContainerSidebar, width }
                : paddingHorizontalStyle,
            containerStyle,
        ]}>
        {(!item?.screen || routeName === item?.screen) &&
            ('noContent' in item ? (item.touchHelperPosition !== 'none' && (<View>
                {item.component}
                <View style={[
                    item.touchHelperPosition === 'right'
                        ? [
                            styles.touchHelperContainerRight,
                            {
                                left: item.touchHelperLeft,
                                top: item.touchHelperYOffset || 0,
                            },
                        ]
                        : [
                            styles.touchHelperContainer,
                            {
                                left: item.touchHelperLeft || 0,
                            },
                        ],
                ]} pointerEvents="box-none">
                  <TouchHelper position={item.touchHelperPosition} onPress={item.touchHelperOnPress || nextStep}/>
                </View>
              </View>)) : (<>
              {item.bubblePosition === 'bottom' && (<>
                  {item.component}
                  {arrow}
                </>)}
              <WalkthroughBubbleContent currentStep={currentStep} numberOfSteps={items.length} title={item.title} description={item.description} onPressDone={onPressDone} onPressNext={nextStep} onPressPrevious={previousStep} onPressReset={onPressReset} hideNumberOfSteps={showWalkthroughV6} cardStyle={selectingSidebar && paddingHorizontal !== undefined
                    ? {
                        marginHorizontal: 0,
                        width: width - paddingHorizontal,
                        marginRight: paddingHorizontal + rem(selectingSidebar ? -16 : 16),
                    }
                    : null}/>
              {item.bubblePosition === 'top' && (<>
                  {arrow}
                  {item.component}
                </>)}
            </>))}
      </View>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        ...StyleSheet.absoluteFillObject,
        zIndex: 2,
    },
    overlay: {
        top: -1000,
        bottom: -1000,
    },
    card: {
        marginHorizontal: rem(16),
    },
    overviewText: {
        flex: 0,
    },
    completeSetup: {
        marginHorizontal: -rem(8),
    },
    itemContainer: {
        position: 'absolute',
        width: '100%',
    },
    itemContainerSidebar: {
        flexDirection: 'row-reverse',
    },
    highlightCard: {
        backgroundColor: colors.background.dark,
    },
    highlightCardShown: {
        opacity: 1,
    },
    highlightCardHidden: {
        opacity: 0,
    },
    highlightCardEveryday: {
        alignSelf: 'flex-start',
        padding: rem(8),
        borderRadius: rem(8),
        marginLeft: rem(23),
        marginTop: rem(8),
    },
    highlightCardOverview: {
        padding: rem(4),
        borderRadius: rem(16),
        marginHorizontal: rem(12),
        backgroundColor: colors.background.dark,
    },
    highlightCardOverviewGraphCard: {
        paddingTop: rem(4),
        paddingHorizontal: rem(4),
        marginHorizontal: rem(12),
        backgroundColor: colors.background.dark,
    },
    highlightCardOverviewRow: {
        padding: rem(4),
        borderRadius: rem(16),
        marginHorizontal: rem(12),
    },
    highlightCardAddAccount: {
        alignSelf: 'flex-start',
        marginLeft: rem(23),
        marginTop: rem(240),
        paddingLeft: rem(9),
    },
    legacyOverviewButtonsRowOffset: {
        marginTop: rem(228),
    },
    highlightCardAddAccountTrack: {
        // When track net worth button is visible, the networth row is slighly larged
        marginTop: rem(245),
    },
    highlightCardSeeAll: {
        backgroundColor: colors.background.dark,
        alignSelf: 'flex-end',
        marginRight: rem(8),
        padding: rem(8),
        borderRadius: rem(8),
    },
    highlightCardSeeAllTop: {
        marginTop: -rem(8),
    },
    highlightCardAnalyticsBudgeting: {
        flex: 1,
        flexDirection: 'row',
        marginHorizontal: rem(16),
        marginTop: rem(40),
    },
    highlightCardAnalyticsBudgetingv6: {
        flex: 1,
        flexDirection: 'row',
        marginHorizontal: rem(16),
    },
    highlightCardCompleteSetup: {
        backgroundColor: colors.background.dark,
        marginHorizontal: rem(8),
        paddingTop: rem(12),
        marginBottom: rem(-24),
    },
    bubbleArrowStart: {
        marginLeft: rem(40),
        alignSelf: 'flex-start',
    },
    bubbleArrowEnd: {
        marginRight: rem(40),
        alignSelf: 'flex-end',
    },
    bubbleArrowCenter: {
        alignSelf: 'center',
        marginLeft: 0,
        marginRight: 0,
    },
    bubbleArrowCardButtonLeft: {
        marginLeft: rem(80),
        alignSelf: 'flex-start',
    },
    bubbleArrowCardButtonRight: {
        marginRight: rem(80),
        alignSelf: 'flex-end',
    },
    bubbleArrowTop: {
        transform: [{ rotate: '-180deg' }],
        marginTop: rem(-1),
        marginBottom: rem(8),
    },
    bubbleArrowLeft: {
        transform: [{ rotate: '-90deg' }],
        marginTop: rem(45),
        marginRight: rem(-2),
        marginBottom: rem(8),
    },
    bubbleArrowBottom: {
        marginBottom: rem(-1),
        marginTop: rem(8),
    },
    touchHelperContainer: {
        left: 0,
        bottom: -rem(44),
        position: 'absolute',
    },
    touchHelperContainerRight: {
        top: 0,
        bottom: 0,
        position: 'absolute',
        justifyContent: 'center',
    },
    everydayTitleRow: {
        flexDirection: 'row',
        paddingTop: rem(6),
        alignItems: 'center',
        paddingHorizontal: rem(32),
    },
    everydayInfoButtonCardView: {
        padding: rem(12),
        marginLeft: -rem(8),
    },
    v6OverviewAddButtonContainer: {
        flexDirection: 'row',
        marginHorizontal: rem(16),
        marginTop: rem(12),
    },
    v6OverviewAddButtonCard: {
        padding: rem(4),
        borderRadius: rem(12),
    },
    noMarginHorizontal: {
        marginHorizontal: 0,
    },
    flex: {
        flex: 1,
    },
    learnCardContainer: {
        padding: rem(4),
        marginLeft: rem(4),
        marginRight: rem(12),
        backgroundColor: colors.background.dark,
    },
    learnCard: {
        margin: 0,
        marginLeft: 0,
        marginBottom: 0,
    },
    row: {
        flexDirection: 'row',
    },
    payTabQuickCTAsCardViewStyles: {
        margin: 0,
    },
    creditDebtSection: {
        marginTop: 0,
        marginHorizontal: 0,
    },
    groupsSectionCardViewStyles: {
        marginBottom: 0,
    },
    groupsSectionTitleContainerStyle: {
        marginHorizontal: 0,
    },
    pickerOptionOverlay: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: rem(50),
        paddingLeft: rem(100),
    },
}));
export default memo(WalkthroughContent);
